import { getSupabaseClient } from "@/lib/supabase";

export type ActivityType = 
  | 'VIEW_COURSE_DETAIL'
  | 'STARTED_BOOKING'
  | 'COMPLETED_BOOKING'
  | 'VIEW_PRODUCT_DETAIL'
  | 'SEARCH_COURSES';

// Activity 타입별 데이터 구조 정의
interface ViewCourseDetailData {
  course_id: string;
  course_name: string;
  viewed_at: string;
}

interface StartedBookingData {
  product_id: number;
  trip_id: number;
  product_name: string;
  seats: number[];
  started_at: string;
}

interface CompletedBookingData {
  booking_id: number;
  order_id: string;
  product_id: number;
  trip_id: number;
  completed_at: string;
}

interface ViewProductDetailData {
  product_id: number;
  product_name: string;
  viewed_at: string;
}

interface SearchCoursesData {
  search_query: string;
  result_count: number;
  searched_at: string;
}

// 모든 activity 데이터의 union type
type ActivityData = 
  | ViewCourseDetailData
  | StartedBookingData
  | CompletedBookingData
  | ViewProductDetailData
  | SearchCoursesData;

/**
 * 사용자 활동을 추적하여 데이터베이스에 기록합니다.
 * 이메일 자동화 시스템에서 사용됩니다.
 */
export async function trackUserActivity(
  activityType: ActivityType,
  activityData: ActivityData
): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    
    // 현재 로그인한 사용자 확인
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      // 로그인하지 않은 사용자는 추적하지 않음
      return;
    }

    // 활동 로그 저장
    const { error } = await supabase.rpc('log_user_activity', {
      p_user_id: user.id,
      p_activity_type: activityType,
      p_activity_data: activityData
    });

    if (error) {
      console.error('Failed to track user activity:', error);
    }
  } catch (error) {
    console.error('Error tracking user activity:', error);
  }
}

/**
 * 코스 상세 페이지 조회 추적
 */
export function trackCourseView(courseId: string, courseName: string) {
  return trackUserActivity('VIEW_COURSE_DETAIL', {
    course_id: courseId,
    course_name: courseName,
    viewed_at: new Date().toISOString()
  });
}

/**
 * 예약 시작 추적
 */
export function trackBookingStart(
  productId: number,
  tripId: number,
  productName: string,
  seats: number[]
) {
  return trackUserActivity('STARTED_BOOKING', {
    product_id: productId,
    trip_id: tripId,
    product_name: productName,
    seats: seats,
    started_at: new Date().toISOString()
  });
}

/**
 * 예약 완료 추적
 */
export function trackBookingComplete(
  bookingId: number,
  orderId: string,
  productId: number,
  tripId: number
) {
  return trackUserActivity('COMPLETED_BOOKING', {
    booking_id: bookingId,
    order_id: orderId,
    product_id: productId,
    trip_id: tripId,
    completed_at: new Date().toISOString()
  });
}

/**
 * 상품 상세 조회 추적
 */
export function trackProductView(productId: number, productName: string) {
  return trackUserActivity('VIEW_PRODUCT_DETAIL', {
    product_id: productId,
    product_name: productName,
    viewed_at: new Date().toISOString()
  });
}

/**
 * 코스 검색 추적
 */
export function trackCourseSearch(searchQuery: string, resultCount: number) {
  return trackUserActivity('SEARCH_COURSES', {
    search_query: searchQuery,
    result_count: resultCount,
    searched_at: new Date().toISOString()
  });
}